import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Typography,
  Chip,
  Box,
  Divider,
} from '@mui/material';
import { X as CloseIcon } from 'phosphor-react';
import { Coupon } from '../../../types/coupons';

interface Props {
  coupon: Coupon;
  onClose: () => void;
}

export default function CouponDetailModal({ coupon, onClose }: Props) {
  const rulesNames: Record<string, string> = {
    FIRST_PURCHASE: 'Primeira compra',
  };

  function getRuleName(rule?: string[] | null) {
    if (!rule || rule.length === 0) return '—';
    return rulesNames[rule[0]] || rule[0];
  }

  const formatDate = (date: string | null) =>
    date ? new Date(date).toLocaleDateString('pt-BR') : '-';

  const weekMap = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

  const Label = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
      {children}
    </Typography>
  );

  const Value = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600 }}>
      {children}
    </Typography>
  );

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 3, pb: 2, fontWeight: 600 }}>
        Detalhes do cupom
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'text.secondary',
          }}
        >
          <CloseIcon size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 0 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <Label>Código:</Label>
            <Value>{coupon.code}</Value>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Label>Descrição:</Label>
            <Value>{coupon.description || '-'}</Value>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Label>Tipo de desconto:</Label>
            <Value>
              {coupon.discountType === 'PERCENTAGE'
                ? 'Porcentagem'
                : 'Valor fixo'}
            </Value>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Label>Valor do desconto:</Label>
            <Value>
              {coupon.discountType === 'PERCENTAGE'
                ? `${coupon.discountValue}%`
                : `R$ ${coupon.discountValue}`}
            </Value>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Label>Serviço:</Label>
            <Value>{coupon.orderType || '-'}</Value>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Label>Limite de uso:</Label>
            <Value>{coupon.usageLimit ?? '-'}</Value>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Label>Usos atuais:</Label>
            <Value>{coupon.usageCount}</Value>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Label>Status:</Label>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={coupon.active ? 'Ativo' : 'Inativo'}
                color={coupon.active ? 'primary' : 'error'}
                size="small"
                sx={{ fontWeight: 600, borderRadius: '6px' }}
              />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          Período de validade
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <Label>Início:</Label>
            <Value>{formatDate(coupon.startDate)}</Value>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Label>Expiração:</Label>
            <Value>{formatDate(coupon.expirationDate)}</Value>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Label>Hora inicial:</Label>
            <Value>{coupon.startHour ?? '-'}</Value>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Label>Hora final:</Label>
            <Value>{coupon.endHour ?? '-'}</Value>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Dias permitidos
          </Typography>
          <Value>
            {coupon.daysOfWeek?.length
              ? coupon.daysOfWeek.map((i) => weekMap[i]).join(', ')
              : '-'}
          </Value>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Regras
          </Typography>
          <Value>{getRuleName(coupon.rule)}</Value>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
